"use client";

import { useEffect, useState } from "react";
import { Star, ThumbsUp } from "lucide-react";
import type { Review } from "@fm/api";

const LIKED_REVIEWS_STORAGE_KEY = "fm:liked-reviews";

function readLikedReviews(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(LIKED_REVIEWS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed) : new Set();
  } catch {
    return new Set();
  }
}

function persistLikedReview(reviewId: string) {
  if (typeof window === "undefined") return;
  const existing = readLikedReviews();
  existing.add(reviewId);
  try {
    window.localStorage.setItem(
      LIKED_REVIEWS_STORAGE_KEY,
      JSON.stringify([...existing]),
    );
  } catch {
    /* ignore quota errors */
  }
}

interface VehicleReviewsProps {
  reviews: Review[];
  averageRating?: number;
  totalReviews?: number;
  isLoading?: boolean;
  isStatsLoading?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  onMarkHelpful?: (reviewId: string) => void;
  markingHelpfulReviewId?: string | null;
}

export function VehicleReviews({
  reviews,
  averageRating = 0,
  totalReviews,
  isLoading = false,
  isStatsLoading = false,
  hasNextPage = false,
  onLoadMore,
  isLoadingMore = false,
  onMarkHelpful,
  markingHelpfulReviewId,
}: VehicleReviewsProps) {
  const [likedReviews, setLikedReviews] = useState<Set<string>>(new Set());

  useEffect(() => {
    setLikedReviews(readLikedReviews());
  }, []);

  const handleMarkHelpful = (reviewId: string) => {
    if (likedReviews.has(reviewId) || !onMarkHelpful) return;
    onMarkHelpful(reviewId);
    persistLikedReview(reviewId);
    setLikedReviews((prev) => new Set(prev).add(reviewId));
  };
  if (isLoading) {
    return (
      <div className="py-8">
        <h2 className="text-2xl font-semibold mb-6 text-[#020817]">Reviews</h2>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-blue-600 rounded-full"></div>
        </div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="py-8">
        <h2 className="text-2xl font-semibold mb-6 text-[#020817]">Reviews</h2>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Star className="w-16 h-16 text-gray-300 mb-4" />
          <p className="text-gray-600 text-lg">
            No reviews yet for this vehicle.
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Be the first to rent and leave a review!
          </p>
        </div>
      </div>
    );
  }

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${
          i < rating
            ? "fill-yellow-400 text-yellow-400"
            : "fill-gray-200 text-gray-200"
        }`}
      />
    ));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
  };

  return (
    <div className="py-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-[#020817]">Reviews</h2>
        <div className="flex items-center gap-2">
          <div className="flex">{renderStars(Math.round(averageRating))}</div>
          <span className="text-lg font-semibold text-[#020817]">
            {averageRating.toFixed(1)}
          </span>
          <span className="text-gray-500">
            ({totalReviews !== undefined ? totalReviews : reviews.length}{" "}
            reviews)
          </span>
        </div>
      </div>

      <div className="space-y-6">
        {reviews.map((review) => (
          <div
            key={review.id}
            className="border-b border-gray-200 pb-6 last:border-b-0"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-[#020817]">
                    {review.customer_name || "Anonymous"}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-500 text-sm">
                    {formatDate(review.created_at)}
                  </span>
                </div>
                <div className="flex">{renderStars(review.rating)}</div>
              </div>
            </div>
            {review.comment && (
              <p className="text-gray-700 leading-relaxed">{review.comment}</p>
            )}
            {onMarkHelpful && (
              <div className="mt-3">
                {(() => {
                  const liked = likedReviews.has(review.id);
                  const pending = markingHelpfulReviewId === review.id;
                  return (
                    <button
                      type="button"
                      onClick={() => handleMarkHelpful(review.id)}
                      disabled={liked || pending}
                      title={liked ? "You already marked this as helpful" : ""}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition disabled:cursor-default ${
                        liked
                          ? "border-blue-200 bg-blue-50 text-blue-700"
                          : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                      } ${pending ? "opacity-50" : ""}`}
                    >
                      <ThumbsUp
                        className={`h-3.5 w-3.5 ${liked ? "fill-blue-500 text-blue-500" : ""}`}
                      />
                      {liked ? "Helpful" : "Mark helpful"}
                      {review.helpful_count > 0 && (
                        <span className="ml-0.5 font-medium text-gray-900">
                          ({review.helpful_count})
                        </span>
                      )}
                    </button>
                  );
                })()}
              </div>
            )}
            {review.reply_text && review.reply_is_public && (
              <div className="mt-3 rounded-md border-l-4 border-blue-500 bg-blue-50/60 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
                  <span className="font-medium text-[#020817]">
                    Response from{" "}
                    {review.replied_by?.name ||
                      review.replied_by?.email ||
                      "the owner"}
                  </span>
                  {review.replied_at && (
                    <span>{formatDate(review.replied_at)}</span>
                  )}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">
                  {review.reply_text}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {hasNextPage && onLoadMore && (
        <div className="mt-2 flex justify-center">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="group flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-full hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm"
          >
            {isLoadingMore ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-blue-600 rounded-full"></div>
                <span>Loading...</span>
              </>
            ) : (
              <>
                <span>Load more reviews</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
